import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// Category colors shared across spend charts (dark-mode friendly).
export const CATEGORY_COLORS: Record<string, string> = {
  llm: "#818cf8", // indigo
  tools: "#34d399", // emerald
  infra: "#fbbf24", // amber
};

export const FALLBACK_COLOR = "#94a3b8";

export interface SeriesDef {
  key: string;
  label: string;
  color: string;
}

export type DailyDatum = { day: string; [key: string]: string | number };

function CurrencyTooltip({
  active,
  payload,
  label,
  formatValue,
}: {
  active?: boolean;
  payload?: readonly { name?: unknown; value?: unknown; color?: unknown }[];
  label?: unknown;
  formatValue: (cents: number) => string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const rows = payload.filter((p) => Number(p.value ?? 0) > 0);
  return (
    <div className="rounded-md border border-border bg-bg/95 px-3 py-2 text-xs shadow-lg">
      {label != null ? <div className="mb-1 font-medium">{String(label)}</div> : null}
      {rows.length === 0 ? (
        <div className="text-muted-fg">No spend</div>
      ) : (
        rows.map((p) => (
          <div key={String(p.name)} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: typeof p.color === "string" ? p.color : undefined }}
                aria-hidden
              />
              {String(p.name ?? "")}
            </span>
            <span className="tabular-nums">{formatValue(Number(p.value ?? 0))}</span>
          </div>
        ))
      )}
    </div>
  );
}

export function DailyStackedChart({
  data,
  series,
  formatValue,
}: {
  data: DailyDatum[];
  series: SeriesDef[];
  formatValue: (cents: number) => string;
}) {
  if (data.length === 0) {
    return <div className="py-12 text-center text-sm text-muted-fg">No spend in this range.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" vertical={false} />
        <XAxis
          dataKey="day"
          tickFormatter={(d: string) => d.slice(5)}
          tick={{ fontSize: 11, fill: "#a1a1aa" }}
          tickLine={false}
          axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
        />
        <YAxis
          tickFormatter={(v: number) => formatValue(v)}
          tick={{ fontSize: 11, fill: "#a1a1aa" }}
          tickLine={false}
          axisLine={false}
          width={64}
        />
        <Tooltip
          content={(props) => <CurrencyTooltip {...props} formatValue={formatValue} />}
          cursor={{ fill: "rgba(255,255,255,0.04)" }}
        />
        {series.map((s) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stackId="spend"
            stroke={s.color}
            fill={s.color}
            fillOpacity={0.25}
            strokeWidth={2}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function CategoryDonut({
  data,
  formatValue,
}: {
  data: Array<{ key: string; label: string; value: number; color: string }>;
  formatValue: (cents: number) => string;
}) {
  const nonZero = data.filter((d) => d.value > 0);
  if (nonZero.length === 0) {
    return <div className="py-12 text-center text-sm text-muted-fg">No spend yet.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={nonZero}
          dataKey="value"
          nameKey="label"
          innerRadius={56}
          outerRadius={84}
          paddingAngle={2}
          stroke="none"
        >
          {nonZero.map((d) => (
            <Cell key={d.key} fill={d.color} />
          ))}
        </Pie>
        <Tooltip content={(props) => <CurrencyTooltip {...props} formatValue={formatValue} />} />
      </PieChart>
    </ResponsiveContainer>
  );
}
